/**
 * BulkChangeProjectTypeDialog
 *
 * Allows changing the project type for one or more selected projects at once.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Loader2, Layers } from 'lucide-react';
import type { ProjectType } from '@/types';

const PROJECT_TYPE_OPTIONS: { value: ProjectType; label: string }[] = [
  { value: 'fixed-price', label: 'Fixed Price' },
  { value: 'time-and-materials', label: 'Time & Materials' },
  { value: 'retainer', label: 'Retainer' },
];

interface BulkChangeProjectTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  /** Called with the chosen project type when the user confirms */
  onConfirm: (projectType: ProjectType) => Promise<void>;
}

export function BulkChangeProjectTypeDialog({
  open,
  onOpenChange,
  selectedCount,
  onConfirm,
}: BulkChangeProjectTypeDialogProps) {
  const [selectedType, setSelectedType] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  const handleConfirm = async () => {
    if (!selectedType) return;
    setIsSaving(true);
    try {
      await onConfirm(selectedType as ProjectType);
      onOpenChange(false);
      setSelectedType('');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!isSaving) {
      onOpenChange(open);
      if (!open) setSelectedType('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <Layers className="h-4 w-4 text-primary" />
            </div>
            <DialogTitle>Change Project Type</DialogTitle>
          </div>
          <DialogDescription>
            Update the project type for{' '}
            <span className="font-semibold text-foreground">
              {selectedCount} {selectedCount === 1 ? 'project' : 'projects'}
            </span>
            . This will overwrite the existing project type on all selected projects.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="bulk-project-type-select">New Project Type</Label>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger id="bulk-project-type-select">
                <SelectValue placeholder="Select a project type…" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedType || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Updating…
              </>
            ) : (
              `Update ${selectedCount} ${selectedCount === 1 ? 'Project' : 'Projects'}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
